import React, { useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

import {
  Typography,
  Box,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from '@material-ui/core';

import NumericalInput from 'on_web/components/shared/NumericalInput/NumericalInput';

import { APP_PRIMARY } from 'colours';

const useStyles = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow:
      'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
  },
  checkedIcon: {
    backgroundColor: APP_PRIMARY,
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
      content: '""',
    },
  },
});

function StyledRadio(props) {
  const classes = useStyles();

  return (
    <Radio
      className={classes.root}
      disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
}

/**
 * Component for height question.
 * @param {Component properties} props
 */
export default function HeightQuestion(props) {
  const [height, setHeight] = useState(props.value);
  const [height2, setHeight2] = useState(props.value2);
  const [selectedMetric, setSelectedMetric] = useState(
    props.metrics[props.selectedMetricIx].metricCode
  );

  const onAnswer = val => {
    setHeight(val);
    if (props.onAnswer) {
      const metricIx = props.metrics[0].metricCode === selectedMetric ? 0 : 1;
      props.onAnswer(val, height2, metricIx);
    }
  };

  const onAnswer2 = val => {
    setHeight2(val);
    if (props.onAnswer) {
      const metricIx = props.metrics[0].metricCode === selectedMetric ? 0 : 1;
      props.onAnswer(height, val, metricIx);
    }
  };

  const handleChange = event => {
    const metricIx = props.metrics[0].metricCode === event.target.value ? 0 : 1;
    setSelectedMetric(event.target.value);
    if (props.onAnswer) {
      props.onAnswer(height, height2, metricIx);
    }
  };

  return (
    <React.Fragment>
      <Box
        px={{ xs: 2, sm: 3, md: 4 }}
        justifyContent="center"
        display="flex"
        style={{ marginBottom: '-10px' }}
      >
        <Typography variant="body1">{props.text}</Typography>
      </Box>

      <Box px={{ xs: 2.5, sm: 3, md: 4 }} pd={{ xs: 2.5, sm: 3, md: 4 }}>
        {props.value != null && (
          <Grid container direction="row" justify="center">
            <Grid item style={{ marginRight: '10px' }}>
              <NumericalInput
                value={
                  props.value <=
                  props.metrics[props.selectedMetricIx].minValues[0]
                    ? undefined
                    : props.value
                }
                onChange={val => onAnswer(val)}
                inputProps={{
                  step: 1,
                  min: props.metrics[props.selectedMetricIx].minValues[0],
                  max: props.metrics[props.selectedMetricIx].maxValues[0],
                  type: 'number',
                  'aria-labelledby': 'input-slider',
                }}
                numericalInputValueValidationMsg={
                  props.metrics[props.selectedMetricIx].validationMsgs[0]
                }
                autoFocus={props.autoFocus}
              />
            </Grid>
            {props.metrics[props.selectedMetricIx].minValues.length > 1 &&
              props.metrics[props.selectedMetricIx].validationMsgs.length >
                1 && (
                <Grid item style={{ marginLeft: '10' }}>
                  <NumericalInput
                    value={
                      props.value2 <=
                      props.metrics[props.selectedMetricIx].minValues[1]
                        ? undefined
                        : props.value2
                    }
                    onChange={val => onAnswer2(val)}
                    inputProps={{
                      step: 1,
                      min: props.metrics[props.selectedMetricIx].minValues[1],
                      max: props.metrics[props.selectedMetricIx].maxValues[1],
                      type: 'number',
                      'aria-labelledby': 'input-slider',
                    }}
                    numericalInputValueValidationMsg={
                      props.metrics[props.selectedMetricIx].validationMsgs[1]
                    }
                    autoFocus={props.autoFocus}
                  />
                </Grid>
              )}
            <Grid item>
              <FormControl
                component="fieldset"
                style={{ marginTop: '10px', marginLeft: '10px' }}
              >
                <RadioGroup
                  aria-label="height"
                  name="height"
                  value={selectedMetric}
                  onChange={handleChange}
                >
                  {props.metrics.map((metric, ix) => {
                    return (
                      <FormControlLabel
                        key={ix}
                        value={metric.metricCode}
                        control={<StyledRadio />}
                        label={metric.metricName}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
    </React.Fragment>
  );
}

HeightQuestion.propTypes = {
  /**
   * Callback function with answer.
   */
  onAnswer: PropTypes.func.isRequired,
  /**
   * Text above input control.
   */
  text: PropTypes.string.isRequired,
  /**
   * Initial height value.
   */
  value: PropTypes.number.isRequired,
  /**
   * Initial value for the second part of the height value (inches when metric unit is ft).
   */
  value2: PropTypes.number,
  /**
   * Selected metric.
   */
  selectedMetricIx: PropTypes.number.isRequired,
  /**
   * Available metrics.
   */
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Minimal height values. For each element of the array, one input element is displayed.
       */
      minValues: PropTypes.arrayOf(PropTypes.number).isRequired,
      /**
       * Array of messages shown when validation fails. Order of elements must corresond to the order of elements in minValues array.
       */
      validationMsgs: PropTypes.arrayOf(PropTypes.string).isRequired,
      /**
       * Metric name.
       */
      metricName: PropTypes.string.isRequired,
      /**
       * Metric code.
       */
      metricCode: PropTypes.string.isRequired,
    })
  ).isRequired,
};
